/**
 * =========================================================================================
 *     Copyright © 2026 Dobler Data Solutions, LLC. ALL RIGHTS RESERVED.
 * =========================================================================================
 *     File            :   capacitiesService.ts
 *     Feature         :   117-operational-dashboards
 *     Description     :   Frontend API service for Capacity Overview dashboard
 *     Constitution    :   v1.12.0
 *     Author          :   Peter Dobler
 *     Created         :   2026-02-05
 * =========================================================================================
 */

import { apiClient } from '../apiClient';
import { useQuery, useMutation, useQueryClient, UseQueryOptions } from '@tanstack/react-query';
import type {
  CapacitySummaryResponse,
  CapacityListResponse,
  Capacity,
  CapacityType,
  HealthStatus,
} from '../../types/operations';

// ============================================================================
// Query Key Factory
// ============================================================================

export const OPERATIONS_CAPACITY_KEYS = {
  all: ['operations', 'capacities'] as const,
  summary: (includeInactive?: boolean) => [...OPERATIONS_CAPACITY_KEYS.all, 'summary', { includeInactive }] as const,
  lists: () => [...OPERATIONS_CAPACITY_KEYS.all, 'list'] as const,
  list: (filters: CapacityListFilters) =>
    [...OPERATIONS_CAPACITY_KEYS.lists(), filters] as const,
  details: () => [...OPERATIONS_CAPACITY_KEYS.all, 'detail'] as const,
  detail: (id: string) => [...OPERATIONS_CAPACITY_KEYS.details(), id] as const,
  alerts: (includeInactive?: boolean) => [...OPERATIONS_CAPACITY_KEYS.all, 'alerts', { includeInactive }] as const,
};

// ============================================================================
// Filter Types
// ============================================================================

export interface CapacityListFilters {
  capacityType?: CapacityType;
  healthStatus?: HealthStatus;
  search?: string;
  includeInactive?: boolean;
  isExternal?: boolean;
  page?: number;
  pageSize?: number;
}

// ============================================================================
// API Functions
// ============================================================================

/**
 * Get aggregated capacity summary
 */
export async function getCapacitySummary(includeInactive?: boolean): Promise<CapacitySummaryResponse> {
  const url = includeInactive
    ? '/api/operations/capacities/summary?includeInactive=true'
    : '/api/operations/capacities/summary';
  const response = await apiClient.get<{ data: CapacitySummaryResponse }>(url);
  return response.data.data;
}

/**
 * Get paginated list of capacities with filtering
 */
export async function getCapacityList(
  filters: CapacityListFilters = {}
): Promise<CapacityListResponse> {
  const params = new URLSearchParams();
  if (filters.capacityType) params.append('capacityType', filters.capacityType);
  if (filters.healthStatus) params.append('healthStatus', filters.healthStatus);
  if (filters.search) params.append('search', filters.search);
  if (filters.includeInactive) params.append('includeInactive', 'true');
  if (filters.isExternal !== undefined) params.append('isExternal', String(filters.isExternal));
  if (filters.page) params.append('page', String(filters.page));
  if (filters.pageSize) params.append('pageSize', String(filters.pageSize));

  const queryString = params.toString();
  const url = queryString
    ? `/api/operations/capacities?${queryString}`
    : '/api/operations/capacities';

  const response = await apiClient.get<{ data: CapacityListResponse }>(url);
  return response.data.data;
}

/**
 * Get capacity detail by ID
 */
export async function getCapacityById(id: string): Promise<Capacity & { workspaces: unknown[]; snapshots: unknown[] }> {
  const response = await apiClient.get<{ data: Capacity & { workspaces: unknown[]; snapshots: unknown[] } }>(
    `/api/operations/capacities/${id}`
  );
  return response.data.data;
}

/**
 * Get critical capacity alerts
 */
export async function getCapacityAlerts(includeInactive?: boolean): Promise<Capacity[]> {
  const url = includeInactive
    ? '/api/operations/capacities/alerts?includeInactive=true'
    : '/api/operations/capacities/alerts';
  const response = await apiClient.get<{ data: Capacity[] }>(url);
  return response.data.data;
}

// ============================================================================
// React Query Hooks
// ============================================================================

/**
 * Hook for capacity summary data (overview cards, pie chart)
 */
export function useCapacitySummary(
  includeInactive?: boolean,
  options?: Omit<UseQueryOptions<CapacitySummaryResponse>, 'queryKey' | 'queryFn'>
) {
  return useQuery({
    queryKey: OPERATIONS_CAPACITY_KEYS.summary(includeInactive),
    queryFn: () => getCapacitySummary(includeInactive),
    staleTime: 30_000, // 30 seconds for live dashboard data
    refetchInterval: 60_000, // Auto-refresh every 60 seconds
    ...options,
  });
}

/**
 * Hook for paginated capacity list
 */
export function useCapacityList(
  filters: CapacityListFilters = {},
  options?: Omit<UseQueryOptions<CapacityListResponse>, 'queryKey' | 'queryFn'>
) {
  return useQuery({
    queryKey: OPERATIONS_CAPACITY_KEYS.list(filters),
    queryFn: () => getCapacityList(filters),
    staleTime: 30_000,
    ...options,
  });
}

/**
 * Hook for capacity detail
 */
export function useCapacityDetail(
  id: string | null,
  options?: Omit<
    UseQueryOptions<Capacity & { workspaces: unknown[]; snapshots: unknown[] }>,
    'queryKey' | 'queryFn'
  >
) {
  return useQuery({
    queryKey: OPERATIONS_CAPACITY_KEYS.detail(id || ''),
    queryFn: () => getCapacityById(id!),
    enabled: !!id,
    staleTime: 30_000,
    ...options,
  });
}

/**
 * Hook for critical capacity alerts
 */
export function useCapacityAlerts(
  includeInactive?: boolean,
  options?: Omit<UseQueryOptions<Capacity[]>, 'queryKey' | 'queryFn'>
) {
  return useQuery({
    queryKey: OPERATIONS_CAPACITY_KEYS.alerts(includeInactive),
    queryFn: () => getCapacityAlerts(includeInactive),
    staleTime: 30_000,
    refetchInterval: 60_000,
    ...options,
  });
}

// ============================================================================
// Mutations
// ============================================================================

export interface CapacityUpdatePayload {
  criticality?: string;
  status?: string;
  billing_tag?: string;
  tenant_key_id?: string;
}

/**
 * Update a capacity's editable fields
 */
export async function updateCapacity(
  id: string,
  payload: CapacityUpdatePayload
): Promise<Capacity & { workspaces: unknown[]; snapshots: unknown[] }> {
  const response = await apiClient.patch<{
    data: Capacity & { workspaces: unknown[]; snapshots: unknown[] };
  }>(`/api/operations/capacities/${id}`, payload);
  return response.data.data;
}

/**
 * Mutation hook for updating a capacity
 */
export function useUpdateCapacity() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: CapacityUpdatePayload }) =>
      updateCapacity(id, payload),
    onSuccess: (_data, variables) => {
      // Invalidate relevant queries
      queryClient.invalidateQueries({ queryKey: OPERATIONS_CAPACITY_KEYS.all });
      queryClient.invalidateQueries({
        queryKey: OPERATIONS_CAPACITY_KEYS.detail(variables.id),
      });
    },
  });
}